// Single source of truth for everything role-related in the UI.
// Anywhere in the app that needs a role label, a role-display order, or
// a "who can create whom" / "which roles have sites" rule should import
// from THIS file. Do not redefine these maps inside feature folders.

export type UserRole =
  | 'admin'
  | 'super_admin'
  | 'field_officer'
  | 'hod'

// Singular display label per role. "HOD" stays uppercase as an acronym;
// every other role is title-cased.
export const ROLE_LABELS: Record<UserRole, string> = {
  field_officer:       'Field Officer',
  hod:                 'HOD',
  admin:               'Admin',
  super_admin:         'Super Admin',
}

// Plural section heading per role for the multi-role users list. Picked to
// read cleanly in uppercase styling — "HEADS OF DEPARTMENT" instead of
// the abbreviation-soup "HODS".
export const ROLE_GROUP_LABELS: Record<UserRole, string> = {
  field_officer:       'Field Officers',
  hod:                 'Heads of Department',
  admin:               'Admins',
  super_admin:         'Super Admins',
}

// Render order for the multi-section list and any "iterate over all roles"
// loop. Operational roles (FO, HOD) come before admin tier.
export const ROLE_RENDER_ORDER: readonly UserRole[] = [
  'field_officer',
  'hod',
  'admin',
  'super_admin',
]

// Roles whose UserCard should render the "Sites Assigned" + "Site count"
// rows. Field officers are the only role with a multi-site assignment
// relation in this product. HOD / Admin / Super Admin have
// different (or no) site relations and should hide those rows.
export const ROLES_WITH_MULTI_SITE_ASSIGNMENT: ReadonlySet<UserRole> = new Set([
  'field_officer',
])

// Who-can-create-whom matrix used by AddOfficerModal. Mirrors the role
// hierarchy decision: super_admin can create anyone; admin can create
// everyone except super_admin; non-admin roles can't create users at all.
export const ROLES_CREATABLE_BY: Record<UserRole, UserRole[]> = {
  super_admin: ['field_officer', 'hod', 'admin', 'super_admin'],
  admin:       ['field_officer', 'hod', 'admin'],
  field_officer: [],
  hod:           [],
}

// Roles a given caller is allowed to TARGET in a role-change PATCH from
// the Edit modal. Same hierarchy as ROLES_CREATABLE_BY (admins still
// can't elevate anyone to super_admin), but the dropdown the user sees
// MUST also include the user's CURRENT role even if technically not in
// this list — otherwise the select displays the wrong option as selected.
// EditOfficerModal handles that union; this map is the "permitted target"
// half.
export const ROLES_TARGETABLE_BY: Record<UserRole, UserRole[]> = {
  super_admin: ['field_officer', 'hod', 'admin', 'super_admin'],
  admin:       ['field_officer', 'hod', 'admin'],
  field_officer: [],
  hod:           [],
}

// Convenience helpers — prefer these over inline `ROLE_LABELS[role]` so
// unknown role values from the backend don't crash the UI.

export function roleLabel(role: UserRole | string | null | undefined): string {
  if (!role) {return '—'}
  return ROLE_LABELS[role as UserRole] ?? String(role)
}

export function roleGroupLabel(role: UserRole | string | null | undefined): string {
  if (!role) {return '—'}
  return ROLE_GROUP_LABELS[role as UserRole] ?? String(role)
}

// Roles that log in with email + password (admin tier). Field officers
// authenticate via phone-OTP, so email is optional for them on both
// create and edit.
export function needsCredentials(role: UserRole): boolean {
  return role === 'admin' || role === 'super_admin' || role === 'hod'
}
